import PropTypes from "prop-types";
import * as React from "react";
import { useDebounce, useShopsSearchDashboard } from "../../../hooks";
import { setSignersOnProducts } from "../../../services/bulkProduct";
import {
  Alert,
  Autocomplete,
  Avatar,
  Button,
  Checkbox,
  CloseIcon,
  Dialog,
  IconButton,
  NumberInput,
  Typography,
} from "../../../themeV2";
import {
  canApply,
  describeProblem,
  isZeroSplit,
  rowFromShop,
  summariseChanges,
  toSignerPayload,
} from "./bulkSigners";
import css from "./BulkSignersDialog.module.css";

const MAX_SPLIT_DOLLARS = 10000;
const CURRENCY = { style: "currency", currency: "USD" };

const SignerRow = ({ row, onChange, onRemove }) => (
  <li>
    <fieldset className={css.fieldset}>
      <legend className={css.legend}>
        <Avatar src={row.avatarUrl} alt="" size="sm" />
        <Typography variant="title-sm" as="span">
          {row.name}
        </Typography>
      </legend>

      <div className={css.controls}>
        <NumberInput
          label="Amount per sale"
          value={row.amount}
          onValueChange={(amount) => onChange({ ...row, amount })}
          min={0}
          max={MAX_SPLIT_DOLLARS}
          step={1}
          format={CURRENCY}
          required
          description={
            isZeroSplit(row)
              ? `${row.name} will earn nothing from these products.`
              : undefined
          }
        />

        <Checkbox
          label="No signature required"
          checked={row.noFulfillment}
          onCheckedChange={(noFulfillment) =>
            onChange({ ...row, noFulfillment })
          }
        />
      </div>

      <IconButton
        className={css.remove}
        variant="text"
        size="sm"
        label={`Remove ${row.name}`}
        onClick={onRemove}
      >
        <CloseIcon />
      </IconButton>
    </fieldset>
  </li>
);

SignerRow.propTypes = {
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  row: PropTypes.shape({
    amount: PropTypes.number,
    avatarUrl: PropTypes.string,
    influencerId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    noFulfillment: PropTypes.bool.isRequired,
  }).isRequired,
};

const BulkSignersDialog = React.memo(function BulkSignersDialog({
  open,
  onClose,
  selectedIds,
  onActionComplete,
}) {
  const [rows, setRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [problems, setProblems] = React.useState([]);
  const [error, setError] = React.useState(null);
  const [changes, setChanges] = React.useState(null);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  useDebounce(() => setDebouncedQuery(searchQuery.trim()), 250, [searchQuery]);

  const chosenIds = React.useMemo(
    () => rows.map((row) => row.influencerId),
    [rows],
  );

  const { shops } = useShopsSearchDashboard(debouncedQuery, {
    excludeCollections: true,
    excludedIds: chosenIds,
  });

  const openRef = React.useRef(open);
  const [internalOpen, setInternalOpen] = React.useState(open);

  React.useEffect(() => {
    if (open !== openRef.current) {
      openRef.current = open;
      setInternalOpen(open);
    }
  }, [open]);

  const names = React.useMemo(
    () => new Map(rows.map((row) => [row.influencerId, row.name])),
    [rows],
  );

  const handleClose = React.useCallback(() => {
    const didChange = changes !== null;

    setRows([]);
    setProblems([]);
    setError(null);
    setChanges(null);
    setSearchQuery("");

    return didChange ? onActionComplete() : onClose();
  }, [changes, onActionComplete, onClose]);

  const handleAddShop = React.useCallback((shop) => {
    setRows((prev) => [...prev, rowFromShop(shop)]);
    setSearchQuery("");
  }, []);

  const handleChangeRow = React.useCallback((next) => {
    setRows((prev) =>
      prev.map((row) => (row.influencerId === next.influencerId ? next : row)),
    );
  }, []);

  const handleRemoveRow = React.useCallback((influencerId) => {
    setRows((prev) => prev.filter((row) => row.influencerId !== influencerId));
  }, []);

  const handleApply = React.useCallback(async () => {
    setIsLoading(true);
    setProblems([]);
    setError(null);

    try {
      const result = await setSignersOnProducts({
        productIds: [...selectedIds],
        signers: rows.map(toSignerPayload),
      });
      setChanges(result.changes);
    } catch (err) {
      setProblems(err.problems ?? []);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedIds, rows]);

  const count = selectedIds.size;
  const plural = count !== 1 ? "s" : "";
  const isDone = changes !== null;

  return (
    <Dialog.Root
      open={internalOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isLoading) setInternalOpen(false);
      }}
      /* Reset after the exit animation, not on close; see BulkTagsDialog. */
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <Dialog.Popup size="md">
        <Dialog.Title>
          Add signers to {count} product{plural}
        </Dialog.Title>

        <Dialog.Body>
          {isDone ? (
            <Typography variant="body-lg" as="p" role="status">
              {summariseChanges(changes)}
            </Typography>
          ) : (
            <>
              <Typography variant="body-md" color="variant" as="p">
                Shops already on a product will have their amount replaced.
              </Typography>

              {rows.length > 0 && (
                <ul className={css.rows}>
                  {rows.map((row) => (
                    <SignerRow
                      key={row.influencerId}
                      row={row}
                      onChange={handleChangeRow}
                      onRemove={() => handleRemoveRow(row.influencerId)}
                    />
                  ))}
                </ul>
              )}

              <div className={css.search}>
                <Autocomplete.Root
                  label="Add a signer"
                  placeholder="Search shops"
                  value={null}
                  onValueChange={(shop) => shop && handleAddShop(shop)}
                  inputValue={searchQuery}
                  onInputValueChange={setSearchQuery}
                  /* Server-matched results; see BulkTagsDialog. */
                  autoComplete="none"
                  itemToStringLabel={(shop) => shop?.name ?? ""}
                >
                  {shops.map((shop) => (
                    <Autocomplete.Item key={shop.influencer_id} value={shop}>
                      <span className={css.result}>
                        <Avatar src={shop.avatar_url} alt="" size="sm" />
                        <Typography variant="body-lg" as="span">
                          {shop.name}
                        </Typography>
                      </span>
                    </Autocomplete.Item>
                  ))}

                  <Autocomplete.Empty>
                    No matching shops found.
                  </Autocomplete.Empty>
                </Autocomplete.Root>
              </div>

              {error && (
                <div className={css.error}>
                  <Alert severity="error" title={error}>
                    {problems.length > 0 && (
                      <ul>
                        {problems.map((problem) => (
                          <li
                            key={`${problem.reason}:${
                              problem.influencerId ?? problem.productId
                            }`}
                          >
                            {describeProblem(names)(problem)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Alert>
                </div>
              )}
            </>
          )}
        </Dialog.Body>

        <Dialog.Actions>
          {isDone ? (
            <Button variant="filled" onClick={() => setInternalOpen(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="text"
                disabled={isLoading}
                onClick={() => setInternalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="filled"
                disabled={!canApply(rows)}
                loading={isLoading}
                onClick={handleApply}
              >
                Apply
              </Button>
            </>
          )}
        </Dialog.Actions>
      </Dialog.Popup>
    </Dialog.Root>
  );
});

BulkSignersDialog.displayName = "BulkSignersDialog";

BulkSignersDialog.propTypes = {
  onActionComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
};

export default BulkSignersDialog;
